<script setup lang="ts">
import merge from 'lodash-es/merge';
import type { ListItemModel } from '../../../pages/cms/modules/articles/_runtime/services/ArticlesService';
import ArticlesService from '../../../pages/cms/modules/articles/_runtime/services/ArticlesService';

defineOptions({
    name: 'WidgetContentWithArticlesSettings'
});

const emit = defineEmits<{
    (e: 'update:modelValue', payload: any): void;
}>();

const props = defineProps<{
    modelValue: any;
}>();

const module = ref('articles');
const defaults = ref<any>({
    morePage: null,
    content: '',
    backgroundImage: null,
    relatedArticles: []
});
const articleId = ref<number>(null);
const related = ref<ListItemModel[]>([]);

const form = computed<any>(() =>
{
    return props.modelValue;
});

function triggerModel(): void
{
    emit('update:modelValue', form.value);
}

async function fetchArticle(id: number): Promise<ListItemModel>
{
    const response = await ArticlesService.fetchAutocompleteModel(id);

    return response || null;
}

async function searchArticles(query: string, limit: number): Promise<ListItemModel[]>
{
    return await ArticlesService.getAutocompleteList(query, limit);
}

async function addRelated(): Promise<void>
{
    if (articleId.value == null)
        return;

    const response = await ArticlesService.fetchAutocompleteModel(articleId.value);

    if (related.value.find(x => x.originId === response.originId) === undefined &&
      form.value.settings.relatedArticles.find((x: any) => x === response.originId) === undefined)
    {
        related.value.push(response);
        form.value.settings.relatedArticles.push(response.originId);
    }

    articleId.value = null;
}

function removeRelated(originId: number): void
{
    related.value = related.value.filter(x => x.originId != originId);
    form.value.settings.relatedArticles = form.value.settings.relatedArticles.filter((x: any) => x != originId);
}

async function loadRelated(id: number): Promise<boolean>
{
    if (id === 0)
        return true;

    try
    {
        related.value = await ArticlesService.getRelatedFromWidget(id);
    }
    catch (ex)
    {
        return false;
    }

    return true;
}

watch(() => form.value.settings, () =>
{
    triggerModel();
}, { deep: true });

onBeforeMount(async (): Promise<void> =>
{
    form.value.settings = merge(defaults.value, form.value.settings);
    loadRelated(form.value.id);
});
</script>

<template>
    <hr>

    <IdeoFormGroup :label="$t('[[[Strona docelowa dla \'Czytaj więcej\']]]')" :invalid-feedback="form.$errors.first('settings.morePage')" :state="form.$errors.state('settings.morePage')">
        <EditoPageSelector
            v-model="form.settings.morePage"
            :module="module"
            label=""
            :website="false"
            :preview="false"
            class="mb-0"
        />
    </IdeoFormGroup>

    <hr>

    <IdeoFormGroup :label="$t('[[[Wybierz artykuł]]]')">
        <div class="d-flex flex-column flex-md-row">
            <div class="flex-grow-1">
                <IdeoAutocomplete
                    v-model="articleId"
                    name="articleId"
                    track-by="id"
                    :placeholder="$t('[[[zacznij pisać aby wyszukać artykuł...]]]')"
                    :fetch="(id: number) => fetchArticle(id)"
                    :search="(query: string, limit: number) => searchArticles(query, limit)"
                >
                    <template #selected="{ option }">
                        <div class="text-wrap">
                            {{ option.title }}
                        </div>
                    </template>
                    <template #option="{ option }">
                        <div class="text-wrap">
                            {{ option.title }}
                        </div>
                    </template>
                </IdeoAutocomplete>
            </div>
            <div class="d-flex align-items-stretch">
                <button class="btn btn-secondary text-nowrap mt-2 mt-md-0 ms-md-2" @click.prevent="addRelated()">
                    <i class="fas fa-plus" />
                </button>
            </div>
        </div>
    </IdeoFormGroup>

    <IdeoFormGroup :label="$t('[[[Powiązane artykuły]]]')">
        <div class="list-group">
            <div v-for="item in related" :key="item.id" class="list-group-item list-group-item-action p-2 d-flex">
                <span class="me-2">
                    <i class="fad fa-fw fa-browser text-primary" />
                </span>
                <span class="me-auto">
                    <span class="d-block" :title="$t('[[[Powiązany artykuł]]]')">
                        <b>{{ item.title }}</b>
                    </span>
                </span>
                <span class="d-flex align-items-center">
                    <IdeoButton :id="`btn-delete-related-${item.originId}`" variant="danger" size="sm" pill :title="$t('[[[Usuń powiązanie]]]')">
                        <i class="fas fa-trash-alt" />
                    </IdeoButton>
                    <Confirmation :message="$t('[[[Potwierdzenie usunięcia]]]')" :target="`btn-delete-related-${item.originId}`" :value="item.id" @confirm="removeRelated(item.originId)" />
                </span>
            </div>
            <div v-if="related.length == 0" class="list-group-item list-group-item-light text-center">
                {{ $t('[[[Brak powiązań]]]') }}
            </div>
        </div>
    </IdeoFormGroup>
    <hr>

    <IdeoFormGroup :label="$t('[[[Treść]]]')" :invalid-feedback="form.$errors.first('settings.content')" :state="form.$errors.state('settings.content')">
        <RichTextEditor id="editorContent" v-model="form.settings.content" />
    </IdeoFormGroup>

    <IdeoFormGroup :label="$t('[[[Zdjęcie w tle]]]')" :invalid-feedback="form.$errors.first(`settings.backgroundImage`)" :state="form.$errors.state(`settings.backgroundImage`)">
        <IdeoFilePicker id="backgroundImage" v-model="form.settings.backgroundImage" media="images" />
    </IdeoFormGroup>
</template>
